import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';

// 공통 컴포넌트
import EmpSelectbox from '../../common/form/autocomplete/EmpSelectbox';
import MasProductModal from '../../common/form/modal/MasProductModal';
import MultiUploadComponent from '../../common/form/MultiUploadComponent';
import { useFormik } from 'formik';
import * as yup from 'yup';

// API
import * as manufacturingApi from '../../../lib/api/manufacturing';

const validationSchema = yup.object({
  productUuid: yup.string().required('제품을 선택해주세요'),
  productName: yup.string().required('제품명이 필요합니다'),
  workflowUuid: yup.string().required('공정 워크플로우를 선택해주세요'),
  // 비우면 0으로 저장돼 출하 대상 수량도 0이 되고 출고를 못 했다(운영 2건).
  planQty: yup
    .number()
    .typeError('생산 수량을 숫자로 입력해주세요')
    .required('생산 수량을 입력해주세요')
    .integer('생산 수량은 정수로 입력해주세요')
    .min(1, '생산 수량은 1 이상이어야 합니다'),
  planStartDate: yup.string().required('생산 시작일을 입력해주세요'),
  expectedCompletionDate: yup
    .string()
    .required('생산 완료 예정일을 입력해주세요')
    // 시작일이 없을 때는 비교하지 않는다. 없는 것을 기준으로 삼으면 늘 걸린다.
    .test('after-start', '완료 예정일이 시작일보다 빠릅니다', function (value) {
      const start = this.parent?.planStartDate;
      if (!value || !start) return true;
      return value >= start;
    }),
  designerUuid: yup.string().required('설계 담당자를 선택해주세요'),
  productionManagerUuid: yup.string().required('생산 담당자를 선택해주세요'),
  qcManagerUuid: yup.string().required('QC 담당자를 선택해주세요'),
});

const ProductionPlanAddDialog = ({ open, onClose, onSave, selectedPlan, projectId, isWorkflowLocked = false }) => {
  /**
   * 생산이 끝난 프로젝트에 계획을 더 붙이는 경우인가.
   *
   * 참이면 사유 칸이 나오고, 안 적으면 저장되지 않는다. 막는 것이 아니라 묻는 것이다 —
   * 다 끝난 프로젝트에 계획이 조용히 하나 더 붙는 일을 없애려는 것이다.
   * 서버도 저장할 때 같은 것을 다시 본다. 여기 답은 칸을 보여줄지 정할 뿐이다.
   */
  const [addReasonRequired, setAddReasonRequired] = useState(false);
  const [openProductModal, setOpenProductModal] = useState(false);
  
  // 워크플로우 목록 (API에서 가져옴)
  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  // 워크플로우를 못 불러왔을 때. 예전에는 가짜 목록으로 대신했다 —
  // 그걸 고르면 없는 워크플로우가 저장돼서 나중에 안 고른 것처럼 보였다.
  const [workflowsError, setWorkflowsError] = useState(null);
  const [selectedWorkflowData, setSelectedWorkflowData] = useState(null);
  
  // 현재 로그인한 사용자 정보
  const [currentUser, setCurrentUser] = useState({
    userId: '',
    userName: '사용자',
  });
  
  // 사용자 정보 불러오기
  useEffect(() => {
    const userInfo = localStorage.getItem('user');
    
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        setCurrentUser({
          userId: user.usrUuid || '',
          userName: user.usrNm || '사용자',
        });
      } catch (e) {
        console.error('사용자 정보 파싱 에러:', e);
      }
    }
  }, []);
  
  // 워크플로우 목록 불러오기
  const fetchWorkflows = useCallback(async () => {
    setWorkflowsLoading(true);
    try {
      const response = await manufacturingApi.getActiveWorkflows();
      console.log('워크플로우 API 응답:', response.data);
      
      if (response.data.success && response.data.data) {
        // API 데이터를 프론트엔드 형식으로 변환
        const workflowList = response.data.data.map((wf) => ({
          id: wf.workflowUuid,
          workflowUuid: wf.workflowUuid,
          workflowCd: wf.workflowCd,
          workflowNm: wf.workflowNm,
          workflowDesc: wf.workflowDesc,
          stepCount: wf.stepCount || 0,
          isDefault: wf.isDefault === 'Y',
          details: wf.details || [],
        }));
        setWorkflows(workflowList);
      } else {
        setWorkflows([]);
        setWorkflowsError('공정 워크플로우를 불러오지 못했습니다.');
      }
    } catch (error) {
      console.error('워크플로우 목록 조회 실패:', error);
      setWorkflows([]);
      setWorkflowsError('공정 워크플로우를 불러오지 못했습니다.');
    } finally {
      setWorkflowsLoading(false);
    }
  }, []);

  
  // 다이얼로그 열릴 때 워크플로우 목록 불러오기
  useEffect(() => {
    if (open) {
      fetchWorkflows();
    }
  }, [open, fetchWorkflows]);

  // 새로 만들 때만 물어본다. 수정은 이미 있는 계획이라 해당 없다.
  useEffect(() => {
    if (!open || selectedPlan || !projectId) {
      setAddReasonRequired(false);
      return;
    }
    let alive = true;
    manufacturingApi.checkPlanAddReasonRequired(projectId)
      .then((res) => {
        if (alive) setAddReasonRequired(Boolean(res.data?.required));
      })
      // 못 물어봤다고 계획을 못 만들게 하지는 않는다. 서버가 마지막에 다시 본다.
      .catch(() => { if (alive) setAddReasonRequired(false); });
    return () => { alive = false; };
  }, [open, selectedPlan, projectId]);

  // 신규 생성 시 기본 워크플로우 자동 선택
  useEffect(() => {
    if (open && !selectedPlan && workflows.length > 0 && !formik.values.workflowUuid) {
      const defaultWf = workflows.find((w) => w.isDefault);
      if (defaultWf) {
        formik.setFieldValue('workflowUuid', defaultWf.workflowUuid);
        setSelectedWorkflowData(defaultWf);
      }
    }
  }, [open, selectedPlan, workflows]);

  const formik = useFormik({
    initialValues: {
      productUuid: '',
      productName: '',
      productSpec: '',
      planQty: '',
      workflowUuid: '',
      planStartDate: '',
      expectedCompletionDate: '',
      // 담당자 UUID (기본값: 로그인 사용자)
      designerUuid: '',
      productionManagerUuid: '',
      qcManagerUuid: '',
      // 담당자명
      designerName: '',
      productionManagerName: '',
      qcManagerName: '',
      remarks: '',
      addReason: '',
      files: [{ file: null }],
    },
    validationSchema: validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      console.log('=== formik onSubmit 호출됨 ===');
      console.log('values:', values);
      console.log('values.productUuid:', values.productUuid);
      console.log('values.designerUuid:', values.designerUuid);
      console.log('values.productionManagerUuid:', values.productionManagerUuid);
      console.log('values.qcManagerUuid:', values.qcManagerUuid);
      
      const planData = {
        ...values,
        // 필수 필드들 명시적으로 포함
        productUuid: values.productUuid,
        productName: values.productName,
        designerUuid: values.designerUuid,
        designerName: values.designerName,
        productionManagerUuid: values.productionManagerUuid,
        productionManagerName: values.productionManagerName,
        qcManagerUuid: values.qcManagerUuid,
        qcManagerName: values.qcManagerName,
        workflowUuid: values.workflowUuid,
        workflowNm: selectedWorkflowData?.workflowNm || '',
        workflowDesc: selectedWorkflowData?.workflowDesc || '',
        supervisor: currentUser.userName,
        supervisorId: currentUser.userId,
      };
      
      console.log('planData:', planData);
      console.log('planData.designerUuid:', planData.designerUuid);
      console.log('planData.productUuid:', planData.productUuid);
      console.log('onSave 호출 직전');
      
      try {
        await onSave(planData);
        console.log('onSave 완료');
        // 성공 시에만 리셋 (onSave에서 다이얼로그를 닫음)
      } catch (error) {
        console.error('onSave 에러:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // 신규 생성일 때 기본 담당자 설정 (로그인 사용자)
  useEffect(() => {
    if (open && !selectedPlan && currentUser.userId) {
      console.log('=== 기본 담당자 설정 ===');
      console.log('currentUser:', currentUser);
      
      // formik values가 비어있을 때만 설정
      const setDefaultManagers = () => {
        if (!formik.values.designerUuid) {
          formik.setFieldValue('designerUuid', currentUser.userId);
          formik.setFieldValue('designerName', currentUser.userName);
        }
        if (!formik.values.productionManagerUuid) {
          formik.setFieldValue('productionManagerUuid', currentUser.userId);
          formik.setFieldValue('productionManagerName', currentUser.userName);
        }
        if (!formik.values.qcManagerUuid) {
          formik.setFieldValue('qcManagerUuid', currentUser.userId);
          formik.setFieldValue('qcManagerName', currentUser.userName);
        }
        console.log('기본 담당자 설정 완료');
        console.log('formik.values after setDefault:', formik.values);
      };
      
      // 약간의 딜레이 후 설정 (formik 초기화 후)
      const timer = setTimeout(setDefaultManagers, 200);
      return () => clearTimeout(timer);
    }
  }, [open, selectedPlan, currentUser.userId, currentUser.userName]);

  useEffect(() => {
    if (selectedPlan && open) {
      console.log('=== 수정 모드: selectedPlan 데이터 설정 ===');
      console.log('selectedPlan:', selectedPlan);
      
      const workflowUuid = selectedPlan.workflowUuid || selectedPlan.templateId || '';
      
      const newValues = {
        productUuid: selectedPlan.productUuid || selectedPlan.productId || '',
        productName: selectedPlan.productName || '',
        productSpec: selectedPlan.productSpec || '',
        planQty: selectedPlan.planQty || selectedPlan.targetQuantity || '',
        workflowUuid: workflowUuid,
        // 서버는 planStartDate / planEndDate 로 준다
        planStartDate: selectedPlan.planStartDate || '',
        expectedCompletionDate: selectedPlan.expectedCompletionDate || selectedPlan.planEndDate || '',
        // 담당자 UUID
        designerUuid: selectedPlan.designerUuid || selectedPlan.designManagerUuid || '',
        productionManagerUuid: selectedPlan.productionManagerUuid || '',
        qcManagerUuid: selectedPlan.qcManagerUuid || '',
        // 담당자명 추가
        designerName: selectedPlan.designerName || selectedPlan.designManagerName || '',
        productionManagerName: selectedPlan.productionManagerName || '',
        qcManagerName: selectedPlan.qcManagerName || '',
        remarks: selectedPlan.remarks || '',
        files: selectedPlan.files || [{ file: null }],
      };
      
      console.log('newValues:', newValues);
      formik.setValues(newValues);
      
      if (workflowUuid && workflows.length > 0) {
        const workflow = workflows.find((w) => w.workflowUuid === workflowUuid || w.id === workflowUuid);
        setSelectedWorkflowData(workflow);
      }
    } else if (!selectedPlan && open) {
      // 신규 생성: date input이 화면상 채워져 보이지만 formik 값은 비어 있어
      // 검증에서 "예정일을 입력해주세요"가 뜨는 문제 방지 → 기본값(오늘) 명시 세팅.
      // resetForm({values})는 enableReinitialize와 충돌(initialValues ref가 바뀌며 다시 ''로 되돌려짐)하므로
      // initialValues를 건드리지 않는 setValues로 초기화 + 오늘 날짜 주입.
      const today = new Date().toISOString().split('T')[0];
      formik.setValues({ ...formik.initialValues, planQty: 1, planStartDate: today, expectedCompletionDate: today });
      setSelectedWorkflowData(null);
    }
  }, [selectedPlan, open, workflows]);

  const handleWorkflowChange = (event) => {
    const workflowUuid = event.target.value;
    formik.setFieldValue('workflowUuid', workflowUuid);
    const workflow = workflows.find((w) => w.workflowUuid === workflowUuid || w.id === workflowUuid);
    setSelectedWorkflowData(workflow);
  };

  const handleProductSelect = (product) => {
    formik.setFieldValue('productUuid', product.productUuid);
    formik.setFieldValue('productName', product.productNm);
    formik.setFieldValue('productSpec', product.modelNm || '');
    setOpenProductModal(false);
  };

  const handleReset = () => {
    formik.resetForm();
    setSelectedWorkflowData(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InventoryIcon color="primary" />
              <Typography variant="h6">
                {selectedPlan ? '생산 계획 수정' : '생산 계획 추가'}
              </Typography>
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* 수정 시 현재 생산계획 표시 */}
          {selectedPlan && (
            <Paper 
              elevation={0} 
              sx={{ 
                mt: 2, 
                p: 1.5, 
                bgcolor: '#e3f2fd', 
                border: '1px solid #2196f3',
                borderRadius: 1 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip 
                  label={selectedPlan.planCode} 
                  size="small" 
                  color="primary"
                  sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedPlan.productName}
                </Typography>
              </Box>
              
              {/* 워크플로우 정보 표시 */}
              {selectedWorkflowData && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #90caf9' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary">
                      적용 워크플로우:
                    </Typography>
                    <Chip 
                      label={selectedWorkflowData.workflowNm} 
                      size="small" 
                      color="success"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    {selectedWorkflowData.workflowDesc}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box component="form" onSubmit={formik.handleSubmit}>
            {/* 기본 정보 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InventoryIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  기본 정보
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="생산 계획 코드"
                    value={selectedPlan?.planCode || '자동 생성'}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="생산 총괄 (작성자)"
                    value={currentUser.userName}
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="제품명 *"
                    value={formik.values.productName}
                    InputProps={{ readOnly: true }}
                    placeholder="제품 선택 버튼을 클릭하세요"
                    size="small"
                    error={formik.touched.productName && Boolean(formik.errors.productName)}
                    helperText={formik.touched.productName && formik.errors.productName}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setOpenProductModal(true)}
                    sx={{ height: '40px' }}
                  >
                    제품 선택
                  </Button>
                </Grid>

                {formik.values.productName && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        규격: {formik.values.productSpec || '없음'}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="생산 수량 *"
                    value={formik.values.planQty}
                    onChange={formik.handleChange}
                    name="planQty"
                    placeholder="예: 1000"
                    error={Boolean(formik.errors.planQty)}
                    helperText={formik.errors.planQty}
                    size="small"
                    InputProps={{
                      inputProps: { min: 1, step: 1 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="생산 시작일 *"
                    value={formik.values.planStartDate}
                    onChange={formik.handleChange}
                    name="planStartDate"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <EventIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />,
                    }}
                    error={formik.touched.planStartDate && Boolean(formik.errors.planStartDate)}
                    helperText={formik.touched.planStartDate && formik.errors.planStartDate}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="생산 완료 예정일 *"
                    value={formik.values.expectedCompletionDate}
                    onChange={formik.handleChange}
                    name="expectedCompletionDate"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <EventIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />,
                    }}
                    error={formik.touched.expectedCompletionDate && Boolean(formik.errors.expectedCompletionDate)}
                    helperText={formik.touched.expectedCompletionDate && formik.errors.expectedCompletionDate}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 담당자 지정 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  담당자 지정
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <EmpSelectbox
                    key={`designer-${formik.values.designerUuid || 'new'}`}
                    formik={formik}
                    name="designerUuid"
                    nameField="designerName"
                    label="설계 담당자 *"
                    variant="outlined"
                    margin="none"
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <EmpSelectbox
                    key={`production-${formik.values.productionManagerUuid || 'new'}`}
                    formik={formik}
                    name="productionManagerUuid"
                    nameField="productionManagerName"
                    label="생산 담당자 *"
                    variant="outlined"
                    margin="none"
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <EmpSelectbox
                    key={`qc-${formik.values.qcManagerUuid || 'new'}`}
                    formik={formik}
                    name="qcManagerUuid"
                    nameField="qcManagerName"
                    label="QC 담당자 *"
                    variant="outlined"
                    margin="none"
                    size="small"
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 생산 진행 설정 - 워크플로우 선택 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SettingsIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  생산 진행 설정
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* 워크플로우 잠금 안내 메시지 */}
              {isWorkflowLocked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    🔒 설계 또는 생산이 진행되어 워크플로우를 변경할 수 없습니다.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    변경이 필요한 경우, 현재 생산계획을 종결 후 새로 생성해주세요.
                  </Typography>
                </Alert>
              )}

              {/* 현재 적용 중인 워크플로우 표시 (수정 모드일 때) */}
              {selectedPlan && selectedPlan.workflowUuid && selectedWorkflowData && !isWorkflowLocked && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary">
                      현재 적용 중:
                    </Typography>
                    <Chip
                      label={selectedWorkflowData.workflowNm}
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {selectedWorkflowData.workflowDesc}
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    <Typography variant="caption">
                      ⚠️ 워크플로우를 변경하면 새로운 라우팅이 생성됩니다. 이전 진행 상태는 보존됩니다.
                    </Typography>
                  </Alert>
                </Paper>
              )}

              {/* 못 불러왔으면 그렇다고 말한다. 예전에는 가짜 목록으로 대신해서,
                  고르면 없는 워크플로우가 저장되고 나중에 안 고른 것처럼 보였다. */}
              {workflowsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {workflowsError} 잠시 후 다시 열어보시고, 계속 안 되면 알려주세요.
                </Alert>
              )}
              {!workflowsLoading && !workflowsError && workflows.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  쓸 수 있는 공정 워크플로우가 없습니다. 공정 관리 &gt; 워크플로우에서 먼저 등록해 주세요.
                </Alert>
              )}

              <FormControl 
                fullWidth 
                size="small"
                error={formik.touched.workflowUuid && Boolean(formik.errors.workflowUuid)}
              >
                <InputLabel>공정 워크플로우 선택 *</InputLabel>
                <Select
                  value={formik.values.workflowUuid}
                  onChange={handleWorkflowChange}
                  label="공정 워크플로우 선택 *"
                  disabled={workflowsLoading || isWorkflowLocked}
                  startAdornment={workflowsLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                >
                  {workflows.map((workflow) => (
                    <MenuItem key={workflow.workflowUuid || workflow.id} value={workflow.workflowUuid || workflow.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {workflow.isDefault && (
                          <CheckCircleIcon fontSize="small" color="success" />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={workflow.isDefault ? 600 : 400}>
                              {workflow.workflowNm}
                            </Typography>
                            {workflow.workflowCd && (
                              <Chip 
                                label={workflow.workflowCd} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                            {selectedPlan && selectedPlan.workflowUuid === (workflow.workflowUuid || workflow.id) && (
                              <Chip 
                                label="현재 적용 중" 
                                size="small" 
                                color="primary" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {workflow.workflowDesc}
                          </Typography>
                        </Box>
                        <Chip label={`${workflow.stepCount}단계`} size="small" />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.workflowUuid && formik.errors.workflowUuid && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formik.errors.workflowUuid}
                  </Typography>
                )}
              </FormControl>

              {/* 선택된 워크플로우 미리보기 */}
              {selectedWorkflowData && (
                <Box sx={{ mt: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #2196F3',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1565C0' }}>
                      {selectedWorkflowData.workflowDesc}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>

            {/* 추가 사유 — 생산이 끝난 프로젝트일 때만 나온다 */}
            {addReasonRequired && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  이 프로젝트는 생산이 완료되었습니다. 계획을 더 만드는 이유를 남겨주세요.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  required
                  label="추가 사유"
                  value={formik.values.addReason}
                  onChange={formik.handleChange}
                  name="addReason"
                  placeholder="예: 고객 추가 주문, 불량 발생으로 재생산"
                  error={Boolean(formik.errors.addReason)}
                  helperText={formik.errors.addReason || '나중에 이 계획이 왜 생겼는지 찾을 때 쓰입니다'}
                />
              </Box>
            )}

            {/* 비고 */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="비고"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                name="remarks"
                placeholder="특이사항이나 추가 정보를 입력하세요"
              />
            </Box>

            {/* 첨부파일 */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachFileIcon color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold">
                  첨부파일
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <MultiUploadComponent index={0} formik={formik} />
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">
            취소
          </Button>
          <Button
            onClick={async () => {
              console.log('=== 저장 버튼 클릭 ===');
              console.log('현재 formik.values:', formik.values);
              
              const errors = await formik.validateForm();
              console.log('Validation errors:', errors);

              // 추가 사유는 yup 스키마로 못 건다. 필요한지가 서버 답에 달려 있어서
              // 화면이 열린 뒤에야 정해지기 때문이다. 그래서 여기서 따로 본다.
              if (addReasonRequired && !String(formik.values.addReason || '').trim()) {
                formik.setFieldError('addReason', '추가 사유를 입력해주세요');
                alert('이 프로젝트는 생산이 완료되었습니다.\n계획을 추가하려면 사유를 입력해주세요.');
                return;
              }

              if (Object.keys(errors).length === 0) {
                console.log('Validation 통과, handleSubmit 호출');
                formik.handleSubmit();
              } else {
                console.log('Validation 실패:', errors);
                formik.setTouched({
                  productUuid: true,
                  productName: true,
                  workflowUuid: true,
                  planStartDate: true,
                  expectedCompletionDate: true,
                  designerUuid: true,
                  productionManagerUuid: true,
                  qcManagerUuid: true,
                });
                // 사용자에게 validation 에러 알림
                const errorMessages = Object.values(errors).join('\n');
                alert(`입력값을 확인해주세요:\n${errorMessages}`);
              }
            }}
            variant="contained"
            color="primary"
            startIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={formik.isSubmitting || workflowsLoading}
          >
            {formik.isSubmitting ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 제품 선택 모달 */}
      <MasProductModal
        title="제품 선택"
        open={openProductModal}
        handleClose={() => setOpenProductModal(false)}
        selectedProduct={handleProductSelect}
      />
    </>
  );
};

export default ProductionPlanAddDialog;
